import { type FC } from 'react';
import { Banknote, DollarSign, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { OtroTramiteRecord } from '../types';

export interface OtroTramiteIndicatorsProps {
  tramite: OtroTramiteRecord;
  onManageCobros?: (tramite: OtroTramiteRecord) => void;
}

const formatCurrency = (amount: number, currency: 'ARS' | 'USD') => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Component to display payment status indicators for ARS and USD
 */
const OtroTramiteIndicators: FC<OtroTramiteIndicatorsProps> = ({ tramite, onManageCobros }) => {
  const hasARSAmount = tramite.montoARS > 0;
  const hasUSDAmount = tramite.montoUSD > 0;

  const isARSPaid = tramite.cobroARS === 'Cobrado';
  const isUSDPaid = tramite.cobroUSD === 'Cobrado';
  const arsStatus = `ARS: ${tramite.cobroARS}`;
  const usdStatus = `USD: ${tramite.cobroUSD}`;

  return (
    <div className="flex items-center gap-3">
      {/* Payment status icons container - fixed width slots for consistent alignment */}
      <div className="flex items-center gap-1.5">
        {/* ARS Payment Status - always reserve space */}
        <div className="w-4">
          {hasARSAmount && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Banknote
                  className={`h-4 w-4 ${isARSPaid ? 'text-green-600' : 'text-orange-500'}`}
                />
              </TooltipTrigger>
              <TooltipContent>
                <div className="text-xs">
                  <div className="font-semibold">
                    {arsStatus}
                  </div>
                  <div className="font-bold">
                    {formatCurrency(tramite.montoARS, 'ARS')}
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* USD Payment Status - always reserve space */}
        <div className="w-4">
          {hasUSDAmount && (
            <Tooltip>
              <TooltipTrigger asChild>
                <DollarSign
                  className={`h-4 w-4 ${isUSDPaid ? 'text-green-600' : 'text-orange-500'}`}
                />
              </TooltipTrigger>
              <TooltipContent>
                <div className="text-xs">
                  <div className="font-semibold">
                    {usdStatus}
                  </div>
                  <div className="font-bold">
                    {formatCurrency(tramite.montoUSD, 'USD')}
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Show placeholder if no amounts */}
        {!hasARSAmount && !hasUSDAmount && (
          <span className="text-xs text-gray-400">-</span>
        )}
      </div>

      {/* Quick link to manage cobros - always aligned */}
      {(hasARSAmount || hasUSDAmount) && onManageCobros && (
        <Button
          type="button"
          variant="link"
          size="xs"
          className="p-0 text-primary whitespace-nowrap h-auto"
          onClick={(e) => {
            e.stopPropagation();
            onManageCobros(tramite);
          }}
        >
          <Wallet className="h-3 w-3 mr-1" />
          Cobrar
        </Button>
      )}
    </div>
  );
};

OtroTramiteIndicators.displayName = 'OtroTramiteIndicators';

export default OtroTramiteIndicators;
